import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { galleryAPI, uploadAPI, SITE_NAMES } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, ArrowLeft, Pencil, Trash2, MoreVertical, Image, Upload, X } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

export const GalleryListAdmin = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    try {
      const response = await galleryAPI.getAlbums(false);
      setAlbums(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst galerie');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await galleryAPI.deleteAlbum(deleteId);
      setAlbums(albums.filter(a => a.id !== deleteId));
      toast.success('Album bylo smazáno');
    } catch (error) {
      toast.error('Nepodařilo se smazat album');
    } finally {
      setDeleteId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Galerie</h1>
          <p className="text-[#64748B]">Správa fotoalb</p>
        </div>
        <Link to="/admin/galerie/nova">
          <Button className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="add-album">
            <Plus size={18} className="mr-2" />
            Nové album
          </Button>
        </Link>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {albums.map((album) => (
          <div key={album.id} className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={`album-card-${album.id}`}>
            <div className="relative aspect-video bg-slate-100">
              {album.cover_image ? (
                <img src={album.cover_image} alt={album.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image size={48} className="text-[#CBD5E1]" />
                </div>
              )}
              <div className="absolute top-2 right-2">
                <span className={`badge ${album.is_published ? 'badge-success' : 'badge-warning'}`}>
                  {album.is_published ? 'Publikováno' : 'Skryto'}
                </span>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg text-[#2C3E50]">{album.title}</h3>
                  <p className="text-sm text-[#64748B]">{album.images?.length || 0} fotek</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical size={16} /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to={`/admin/galerie/${album.id}`} className="cursor-pointer">
                        <Pencil size={14} className="mr-2" />Upravit
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDeleteId(album.id)} className="cursor-pointer text-red-600">
                      <Trash2 size={14} className="mr-2" />Smazat
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        ))}

        {albums.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#64748B]">
            Žádná alba
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat album?</AlertDialogTitle>
            <AlertDialogDescription>Tato akce je nevratná. Všechny fotky v albu budou také smazány.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Smazat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const GalleryFormAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [album, setAlbum] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image: '',
    is_published: false,
    site: 'ms'
  });
  const [addImageDialogOpen, setAddImageDialogOpen] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageCaption, setNewImageCaption] = useState('');
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total }

  useEffect(() => {
    if (isEdit) loadAlbum();
  }, [id]);

  const loadAlbum = async () => {
    try {
      const response = await galleryAPI.getAlbum(id);
      setAlbum(response.data);
      setFormData({
        title: response.data.title,
        description: response.data.description || '',
        cover_image: response.data.cover_image || '',
        is_published: response.data.is_published,
        site: response.data.site || 'ms'
      });
    } catch (error) {
      toast.error('Nepodařilo se načíst album');
      navigate('/admin/galerie');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await galleryAPI.updateAlbum(id, formData);
        toast.success('Album bylo aktualizováno');
      } else {
        const response = await galleryAPI.createAlbum(formData);
        toast.success('Album bylo vytvořeno');
        navigate(`/admin/galerie/${response.data.id}`);
        return;
      }
      loadAlbum();
    } catch (error) {
      toast.error('Nepodařilo se uložit album');
    } finally {
      setSaving(false);
    }
  };

  const handleAddImage = async () => {
    if (!newImageUrl) return;
    
    try {
      await galleryAPI.addImage(id, { album_id: id, url: newImageUrl, caption: newImageCaption });
      toast.success('Obrázek byl přidán');
      setNewImageUrl('');
      setNewImageCaption('');
      setAddImageDialogOpen(false);
      loadAlbum();
    } catch (error) {
      toast.error('Nepodařilo se přidat obrázek');
    }
  };

  const handleBulkUpload = async (files) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    setBulkProgress({ done: 0, total: list.length, failed: 0 });
    let failed = 0;
    // Limit paralelismu na 3 současné uploady — šetrné k mobilu i serveru
    const limit = 3;
    let idx = 0;
    const worker = async () => {
      while (idx < list.length) {
        const current = idx++;
        const file = list[current];
        try {
          const up = await uploadAPI.upload(file);
          await galleryAPI.addImage(id, { album_id: id, url: up.data.url, caption: '' });
        } catch (e) {
          failed += 1;
        }
        setBulkProgress(prev => prev ? { ...prev, done: prev.done + 1, failed } : prev);
      }
    };
    await Promise.all(Array.from({ length: Math.min(limit, list.length) }, () => worker()));
    setBulkProgress(null);
    setAddImageDialogOpen(false);
    setNewImageUrl('');
    setNewImageCaption('');
    if (failed === 0) {
      toast.success(`Přidáno ${list.length} fotek`);
    } else if (failed < list.length) {
      toast.warning(`Přidáno ${list.length - failed} z ${list.length} fotek (${failed} selhalo)`);
    } else {
      toast.error('Nepodařilo se nahrát žádnou fotku');
    }
    loadAlbum();
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await galleryAPI.deleteImage(imageId);
      toast.success('Obrázek byl smazán');
      loadAlbum();
    } catch (error) {
      toast.error('Nepodařilo se smazat obrázek');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/galerie" className="text-[#64748B] hover:text-[#4ECDC4]"><ArrowLeft size={24} /></Link>
        <h1 className="text-2xl font-bold text-[#2C3E50]">{isEdit ? 'Upravit album' : 'Nové album'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="title">Název alba *</Label>
          <Input id="title" name="title" value={formData.title} onChange={handleChange} required className="rounded-xl" data-testid="album-title" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Popis</Label>
          <Textarea id="description" name="description" value={formData.description} onChange={handleChange} rows={3} className="rounded-xl resize-none" data-testid="album-description" />
        </div>

        <div className="space-y-2">
          <Label>Titulní obrázek</Label>
          <div className="flex gap-2 items-center">
            {formData.cover_image && (
              <img src={formData.cover_image} alt="Cover" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
            )}
            <label className="cursor-pointer flex-1">
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                setUploading(true);
                try { const res = await uploadAPI.upload(file); setFormData(prev => ({ ...prev, cover_image: res.data.url })); }
                catch (err) { console.error(err); } finally { setUploading(false); }
              }} />
              <Button type="button" variant="outline" className="w-full rounded-xl" disabled={uploading} asChild>
                <span><Upload size={16} className="mr-2" />{uploading ? 'Nahrávání...' : formData.cover_image ? 'Zmenit' : 'Nahrát titulní obrázek'}</span>
              </Button>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Sekce</Label>
          <Select value={formData.site || 'ms'} onValueChange={(value) => setFormData(prev => ({ ...prev, site: value }))}>
            <SelectTrigger className="rounded-xl" data-testid="album-site-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ms">{SITE_NAMES.ms}</SelectItem>
              <SelectItem value="ds">{SITE_NAMES.ds}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <div className="font-medium text-[#2C3E50]">Publikovat</div>
            <div className="text-sm text-[#64748B]">Zobrazit na webu</div>
          </div>
          <Switch checked={formData.is_published} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))} data-testid="album-publish-switch" />
        </div>

        <Button type="submit" disabled={saving} className="w-full rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="album-save">
          {saving ? 'Ukládání...' : (isEdit ? 'Uložit změny' : 'Vytvořit album')}
        </Button>
      </form>

      {/* Images Section - only for edit */}
      {isEdit && album && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-[#2C3E50]">Fotky v albu ({album.images?.length || 0})</h2>
            <Dialog open={addImageDialogOpen} onOpenChange={setAddImageDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="add-image-btn">
                  <Plus size={16} className="mr-2" />
                  Přidat fotky
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Přidat fotky</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Hromadný upload */}
                  <div className="space-y-2 p-4 rounded-xl bg-gradient-to-r from-[#4ECDC4]/10 to-[#95E1D3]/10 border border-[#4ECDC4]/30">
                    <Label className="flex items-center gap-2">
                      <Upload size={14} className="text-[#4ECDC4]" />
                      Hromadné nahrání více fotek najednou
                    </Label>
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        disabled={bulkProgress != null}
                        onChange={(e) => { handleBulkUpload(e.target.files); e.target.value = ''; }}
                        data-testid="bulk-upload-input"
                      />
                      <Button type="button" className="w-full rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5] text-white" disabled={bulkProgress != null} asChild>
                        <span>
                          <Upload size={16} className="mr-2" />
                          {bulkProgress
                            ? `Nahrávám ${bulkProgress.done} / ${bulkProgress.total}…`
                            : 'Vybrat více fotek'}
                        </span>
                      </Button>
                    </label>
                    {bulkProgress && (
                      <div className="h-2 bg-white rounded-full overflow-hidden" data-testid="bulk-progress">
                        <div
                          className="h-full bg-gradient-to-r from-[#4ECDC4] to-[#95E1D3] transition-all"
                          style={{ width: `${(bulkProgress.done / Math.max(1, bulkProgress.total)) * 100}%` }}
                        />
                      </div>
                    )}
                    <p className="text-xs text-[#64748B]">Funguje i na telefonu — můžete označit více fotek najednou.</p>
                  </div>

                  <div className="relative flex items-center gap-3 py-2">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-[#94A3B8] uppercase font-medium">nebo přidat jednu s popiskem</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>

                  <div className="space-y-2">
                    <Label>Vybrat fotku</Label>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        setUploading(true);
                        try { const res = await uploadAPI.upload(file); setNewImageUrl(res.data.url); }
                        catch (err) { console.error(err); } finally { setUploading(false); }
                      }} />
                      <Button type="button" variant="outline" className="w-full rounded-xl" disabled={uploading} asChild>
                        <span><Upload size={16} className="mr-2" />{uploading ? 'Nahrávání...' : newImageUrl ? 'Změnit fotku' : 'Vybrat fotku'}</span>
                      </Button>
                    </label>
                    {newImageUrl && <img src={newImageUrl} alt="Preview" className="mt-2 rounded-xl max-h-32 object-cover" />}
                  </div>
                  <div className="space-y-2">
                    <Label>Popisek</Label>
                    <Input
                      value={newImageCaption}
                      onChange={(e) => setNewImageCaption(e.target.value)}
                      placeholder="Volitelný popisek"
                      className="rounded-xl"
                      data-testid="new-image-caption"
                    />
                  </div>
                  <Button onClick={handleAddImage} disabled={!newImageUrl} className="w-full rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="confirm-add-image">
                    Přidat jednu s popiskem
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {album.images?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {album.images.map((image) => (
                <div key={image.id} className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100">
                  <img src={image.url} alt={image.caption || ''} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      onClick={() => handleDeleteImage(image.id)}
                      className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  {image.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white text-xs truncate">{image.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-[#64748B]">
              <Image size={48} className="mx-auto mb-4 text-[#CBD5E1]" />
              <p>Album je prázdné</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GalleryListAdmin;
